import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { esAutomatica, fechaNegocio, promocionSuperpuesta } from '@tanteo/compartido';
import type { AlcancePromocion, Producto } from '@tanteo/compartido';
import type { DescuentoDelNegocio, DescuentoNuevo, FormaDescuento } from '../../lib/api-descuentos';
import { Boton, Campo, CampoLista, CampoNumero, FilaAcciones, GrupoOpciones, MensajeError } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { alcanceDe, diasDe, fechasDe, valorDe } from './textosDescuento';

const LETRAS_DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

type Edicion = { descuento: DescuentoDelNegocio | null } | null;
type Lugar = 'venta' | 'productos';

/** Una fila de la ventana: el nombre de la sección a la izquierda y sus controles a la derecha. */
function Seccion({ titulo, children, e2e }: { titulo: string; children: ReactNode; e2e?: string }) {
  return (
    <div className="border-border grid gap-2 border-t pt-3 sm:grid-cols-[7rem_1fr]" data-e2e={e2e}>
      <span className="text-muted pt-2 text-etiqueta font-bold uppercase">{titulo}</span>
      <div className="min-w-0 space-y-3">{children}</div>
    </div>
  );
}

function Casilla({
  marcada,
  onCambio,
  apagada = false,
  children,
  e2e,
}: {
  marcada: boolean;
  onCambio: (v: boolean) => void;
  apagada?: boolean;
  children: ReactNode;
  e2e?: string;
}) {
  return (
    <label className={`flex items-center gap-2 font-semibold ${apagada ? 'text-muted cursor-not-allowed' : ''}`}>
      <input type="checkbox" checked={marcada} disabled={apagada} onChange={(e) => onCambio(e.target.checked)} data-e2e={e2e} />
      {children}
    </label>
  );
}

/** La razón o la ayuda de un control. */
function Razon({ children, e2e }: { children: ReactNode; e2e?: string }) {
  return (
    <span className="text-muted text-contenido" data-e2e={e2e}>
      ({children})
    </span>
  );
}

/** Los tipos que tienen sentido en cada lugar: al total no hay unidades que llevar ni precio por unidad. */
const TIPOS: Record<Lugar, { valor: FormaDescuento; texto: string }[]> = {
  venta: [
    { valor: 'porcentaje', texto: 'Porcentaje' },
    { valor: 'monto_fijo', texto: 'Monto al total' },
    { valor: 'libre', texto: 'Libre' },
  ],
  productos: [
    { valor: 'porcentaje', texto: 'Porcentaje' },
    { valor: 'monto_fijo', texto: 'Monto por unidad' },
    { valor: 'lleva_paga', texto: 'Lleva y paga' },
    { valor: 'libre', texto: 'Libre' },
  ],
};

/**
 * Crear o editar un descuento. Primero dónde: al total de la venta o a productos, y en productos a cuáles. A
 * productos, con fechas o días es automático y se aplica solo; lo que así queda decidido se ve apagado.
 */
export function VentanaDescuento({
  edicion,
  descuentos,
  productos,
  ocupado,
  onCerrar,
  onGuardar,
}: {
  edicion: Edicion;
  /** Los demás descuentos del negocio, para avisar si un automático choca con otro. */
  descuentos: DescuentoDelNegocio[];
  productos: Producto[];
  ocupado: boolean;
  onCerrar: () => void;
  onGuardar: (datos: DescuentoNuevo, alFallar: (mensaje: string) => void) => void;
}) {
  const previo = edicion?.descuento ?? null;
  const [nombre, setNombre] = useState('');
  const [lugar, setLugar] = useState<Lugar>('productos');
  const [forma, setForma] = useState<FormaDescuento>('porcentaje');
  const [valor, setValor] = useState<number | null>(null);
  const [lleva, setLleva] = useState<number | null>(2);
  const [paga, setPaga] = useState<number | null>(1);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [dias, setDias] = useState<number[]>([]);
  const [porCategoria, setPorCategoria] = useState(true);
  const [alcance, setAlcance] = useState<AlcancePromocion[]>([]);
  const [maximo, setMaximo] = useState<number | null>(null);
  const [paraPropietario, setParaPropietario] = useState(true);
  const [paraCajero, setParaCajero] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!edicion) return;
    setNombre(previo?.nombre ?? '');
    setLugar(previo && previo.enVenta && !previo.enProducto ? 'venta' : 'productos');
    setForma(previo?.forma ?? 'porcentaje');
    setValor(previo?.valor ?? null);
    setLleva(previo?.lleva ?? 2);
    setPaga(previo?.paga ?? 1);
    setDesde(previo?.desde ?? '');
    setHasta(previo?.hasta ?? '');
    setDias(previo?.dias ?? []);
    setAlcance(previo?.alcance ?? []);
    setPorCategoria(previo ? previo.alcance.every((a) => a.categoria !== null) : true);
    setMaximo(previo?.maxVeces ?? previo?.maxUnidades ?? null);
    setParaPropietario(previo?.paraPropietario ?? true);
    setParaCajero(previo?.paraCajero ?? true);
    setError('');
  }, [edicion, previo]);

  const aProductos = lugar === 'productos';
  const automatico = aProductos && esAutomatica({ desde: desde || null, hasta: hasta || null, dias });
  const categorias = useMemo(() => [...new Set(productos.map((p) => p.categoria.toUpperCase()))].sort(), [productos]);
  const nombreDeProducto = (id: string) => productos.find((p) => p.id === id)?.nombre ?? 'Producto';
  const categoriaDe = (id: string) => productos.find((p) => p.id === id)?.categoria;

  const datos: DescuentoNuevo = {
    nombre,
    forma,
    valor: forma === 'porcentaje' || forma === 'monto_fijo' ? valor : null,
    lleva: forma === 'lleva_paga' ? lleva : null,
    paga: forma === 'lleva_paga' ? paga : null,
    desde: aProductos ? desde || null : null,
    hasta: aProductos ? hasta || null : null,
    dias: aProductos ? dias : [],
    alcance: aProductos ? alcance : [],
    maxVeces: aProductos && forma === 'lleva_paga' ? maximo : null,
    maxUnidades: aProductos && (forma === 'porcentaje' || forma === 'monto_fijo') ? maximo : null,
    enProducto: aProductos,
    enVenta: !aProductos,
    paraPropietario: automatico || paraPropietario,
    paraCajero: automatico || paraCajero,
  };
  const choque = automatico
    ? promocionSuperpuesta({ ...datos, id: previo?.id ?? null }, descuentos, categoriaDe, fechaNegocio())
    : null;

  const valorListo =
    forma === 'libre' ||
    (forma === 'lleva_paga' ? lleva !== null && paga !== null && paga >= 1 && lleva > paga : valor !== null && valor > 0 && (forma !== 'porcentaje' || valor <= 100));
  const impedimento =
    forma === 'libre' && automatico
      ? 'El libre lo escribe el cajero: quítale las fechas y los días, o elige otro tipo.'
      : aProductos && desde && hasta && desde > hasta
        ? 'La fecha final va después de la inicial.'
        : !datos.paraPropietario && !datos.paraCajero
          ? 'Marca quién puede darlo.'
          : '';
  const listo = nombre.trim().length > 0 && valorListo && !impedimento && !choque && !ocupado;
  const cambiaValor = previo !== null && previo.forma !== 'libre' && (forma !== previo.forma || valor !== previo.valor);

  function elegirLugar(l: Lugar) {
    setLugar(l);
    if (!TIPOS[l].some((t) => t.valor === forma)) setForma('porcentaje');
    setMaximo(null);
  }

  function agregarAlcance(v: string) {
    if (!v) return;
    const nuevo: AlcancePromocion = porCategoria ? { productoId: null, categoria: v } : { productoId: v, categoria: null };
    if (alcance.some((a) => a.productoId === nuevo.productoId && a.categoria === nuevo.categoria)) return;
    setAlcance([...alcance, nuevo]);
  }

  return (
    <Modal abierto={edicion !== null} titulo={previo ? 'Editar descuento' : 'Nuevo descuento'} onCerrar={onCerrar}>
      <div className="space-y-3" data-e2e="ventana-descuento">
        <Seccion titulo="Nombre">
          <Campo
            placeholder="Cliente frecuente"
            maxLength={60}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            aria-label="Nombre"
            data-e2e="descuento-nuevo-nombre"
          />
        </Seccion>

        <Seccion titulo="Dónde" e2e="descuento-alcance">
          <GrupoOpciones
            etiqueta="Dónde se usa"
            valor={lugar}
            onElegir={elegirLugar}
            opciones={[
              { valor: 'venta', texto: 'Al total de la venta (F8)' },
              { valor: 'productos', texto: 'A productos (%)' },
            ]}
            e2e={(o) => `descuento-lugar-${o.valor}`}
          />
          {aProductos && (
            <div className="border-border space-y-3 border-l-2 pl-4">
              <GrupoOpciones
                etiqueta="A qué productos"
                valor={porCategoria ? 'categorias' : 'productos'}
                onElegir={(v) => {
                  setPorCategoria(v === 'categorias');
                  setAlcance([]);
                }}
                opciones={[
                  { valor: 'categorias', texto: 'Categorías' },
                  { valor: 'productos', texto: 'Productos' },
                ]}
                e2e={(o) => `descuento-alcance-${o.valor}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                {alcance.map((a) => (
                  <span
                    key={a.productoId ?? a.categoria}
                    className="border-primary bg-primary/15 text-primary rounded-control inline-flex min-h-(--alto-control) items-center gap-2 border px-3 text-contenido font-bold"
                  >
                    {a.categoria ?? nombreDeProducto(a.productoId ?? '')}
                    <button
                      aria-label={`Quitar ${a.categoria ?? nombreDeProducto(a.productoId ?? '')}`}
                      onClick={() => setAlcance(alcance.filter((x) => x !== a))}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                <CampoLista
                  className="max-w-64"
                  value=""
                  onChange={(e) => agregarAlcance(e.target.value)}
                  aria-label={porCategoria ? 'Agregar categoría' : 'Agregar producto'}
                  data-e2e="descuento-alcance-agregar"
                >
                  <option value="">+ agregar</option>
                  {porCategoria
                    ? categorias.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))
                    : productos
                        .filter((p) => p.activo)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre}
                          </option>
                        ))}
                </CampoLista>
                {alcance.length === 0 && <Razon>vacío = todos los productos</Razon>}
              </div>
            </div>
          )}
        </Seccion>

        <Seccion titulo="Tipo">
          <div className="flex flex-wrap items-center gap-2">
            <GrupoOpciones
              etiqueta="Tipo"
              valor={forma}
              onElegir={(f) => {
                setForma(f);
                setValor(null);
                setMaximo(null);
              }}
              opciones={TIPOS[lugar].map((t) => ({ ...t, apagada: t.valor === 'libre' && automatico }))}
              e2e={(o) => `descuento-forma-${o.valor}`}
            />
            {automatico && <Razon>el libre lo escribe el cajero</Razon>}
          </div>
          {(forma === 'porcentaje' || forma === 'monto_fijo') && (
            <CampoNumero
              className="max-w-48"
              etiqueta={forma === 'porcentaje' ? 'Porcentaje %' : aProductos ? 'Monto por unidad $' : 'Monto al total $'}
              plata={forma === 'monto_fijo'}
              min={0}
              max={forma === 'porcentaje' ? 100 : undefined}
              valor={valor}
              onValor={setValor}
              data-e2e="descuento-nuevo-valor"
            />
          )}
          {forma === 'libre' && <p className="text-muted text-contenido">El cajero escribe el valor en el mostrador.</p>}
          {forma === 'lleva_paga' && (
            <div className="flex gap-3">
              <CampoNumero className="max-w-28" etiqueta="Lleva" min={2} valor={lleva} onValor={setLleva} data-e2e="descuento-lleva" />
              <CampoNumero className="max-w-28" etiqueta="Paga" min={1} valor={paga} onValor={setPaga} data-e2e="descuento-paga" />
            </div>
          )}
        </Seccion>

        {aProductos && (
          <Seccion titulo="Cuándo">
            <div className="flex flex-wrap gap-3">
              <Campo className="max-w-48" etiqueta="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-e2e="descuento-desde" />
              <Campo className="max-w-48" etiqueta="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-e2e="descuento-hasta" />
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Días">
              <span className="text-muted mr-1 text-etiqueta font-bold">Días</span>
              {LETRAS_DIAS.map((letra, i) => {
                const dia = i + 1;
                const activo = dias.includes(dia);
                return (
                  <button
                    key={letra}
                    aria-pressed={activo}
                    onClick={() => setDias(activo ? dias.filter((d) => d !== dia) : [...dias, dia].sort())}
                    data-e2e={`descuento-dia-${dia}`}
                    className={`rounded-control min-h-(--alto-control) w-10 border text-contenido font-bold ${
                      activo ? 'border-primary bg-primary/15 text-primary' : 'border-border bg-card text-muted hover:text-foreground'
                    }`}
                  >
                    {letra}
                  </button>
                );
              })}
              <Razon>ninguno = todos</Razon>
            </div>
            <p className="text-muted text-contenido" data-e2e="descuento-modo">
              {automatico
                ? '→ Con fechas o días: descuento automático, se aplica solo sin que el cajero haga nada.'
                : '→ Sin fechas ni días: descuento manual, el cajero lo elige con %.'}
            </p>
          </Seccion>
        )}

        <Seccion titulo="Quién">
          <div className="flex flex-wrap items-center gap-5">
            <Casilla marcada={datos.paraPropietario} onCambio={setParaPropietario} apagada={automatico} e2e="descuento-para-propietario">
              Propietario
            </Casilla>
            <Casilla marcada={datos.paraCajero} onCambio={setParaCajero} apagada={automatico} e2e="descuento-nuevo-cajero">
              Cajero
            </Casilla>
            {automatico && <Razon e2e="descuento-quien-razon">se aplica solo, a quien esté vendiendo</Razon>}
          </div>
        </Seccion>

        {aProductos && (
          <Seccion titulo="Límite">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`font-semibold ${forma === 'libre' ? 'text-muted' : ''}`}>Máximo</span>
              <CampoNumero
                className="w-20"
                min={1}
                valor={forma === 'libre' ? null : maximo}
                onValor={setMaximo}
                disabled={forma === 'libre'}
                aria-label="Máximo"
                data-e2e="descuento-maximo"
              />
              <span className={`font-semibold ${forma === 'libre' ? 'text-muted' : ''}`}>
                {forma === 'lleva_paga' ? 'veces por venta' : 'unidades con descuento por venta'}
              </span>
              <Razon>{forma === 'libre' ? 'el libre no lleva límite' : 'vacío = sin límite'}</Razon>
            </div>
          </Seccion>
        )}

        {choque && (
          <p className="bg-warning/15 text-warning rounded-control px-3 py-2 text-contenido font-semibold" role="alert" data-e2e="descuento-choque">
            ⚠ Choca con «{choque.nombre}»: ya aplica a {choque.alcance.length > 0 ? alcanceDe(choque.alcance, nombreDeProducto) : 'todos los productos'}
            {[diasDe(choque.dias) && ` los ${diasDe(choque.dias).toLowerCase()}`, fechasDe(choque) && `, ${fechasDe(choque)}`]
              .filter(Boolean)
              .join('')}
            . Cambia las fechas, los días o los productos.
          </p>
        )}
        {impedimento && !choque && (
          <p className="text-muted text-contenido" data-e2e="descuento-impedimento">
            {impedimento}
          </p>
        )}
        {cambiaValor && previo && (
          <p className="bg-surface rounded-control text-muted px-3 py-2 text-contenido" data-e2e="descuento-aviso-cambio">
            Desde ahora el mostrador da {valorDe(datos)}. Las ventas ya hechas conservan el {valorDe(previo)} con el que se vendieron.
          </p>
        )}
        <MensajeError>{error}</MensajeError>
        <FilaAcciones>
          <Boton variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton disabled={!listo} onClick={() => onGuardar(datos, setError)} data-e2e="agregar-descuento">
            {previo ? 'Guardar cambios' : 'Crear descuento'}
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
